import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Users, Trophy, Plus, Copy, Lock, Globe, TrendingUp, Medal } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface BettingPool {
  id: string;
  name: string;
  description: string;
  pool_type: string;
  invite_code: string;
  max_members: number;
  entry_fee: number;
  prize_pool: number;
  starts_at: string;
  ends_at: string;
  status: string;
  member_count?: number;
  user_rank?: number;
}

interface PoolMember {
  id: string;
  user_id: string;
  role: string;
  score: number;
  rank: number;
  total_bets: number;
  winning_bets: number;
  profile?: {
    display_name: string;
    avatar_url: string;
  };
}

export function BettingPools() {
  const { user } = useAuth();
  const [pools, setPools] = useState<BettingPool[]>([]);
  const [myPools, setMyPools] = useState<BettingPool[]>([]);
  const [selectedPool, setSelectedPool] = useState<BettingPool | null>(null);
  const [poolMembers, setPoolMembers] = useState<PoolMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [joinCode, setJoinCode] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newPoolName, setNewPoolName] = useState('');
  const [newPoolDesc, setNewPoolDesc] = useState('');

  useEffect(() => {
    fetchPools();
  }, [user]);

  const fetchPools = async () => {
    try {
      // Fetch public pools
      const { data: publicPools } = await supabase
        .from('betting_pools')
        .select('*')
        .eq('pool_type', 'public')
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      setPools(publicPools || []);

      if (user) {
        // Fetch user's pools
        const { data: memberPools } = await supabase
          .from('pool_members')
          .select('pool_id')
          .eq('user_id', user.id);

        if (memberPools && memberPools.length > 0) {
          const poolIds = memberPools.map(m => m.pool_id);
          const { data: userPools } = await supabase
            .from('betting_pools')
            .select('*')
            .in('id', poolIds);

          setMyPools(userPools || []);
        }
      }
    } catch (error) {
      console.error('Error fetching pools:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPoolMembers = async (poolId: string) => {
    const { data } = await supabase
      .from('pool_members')
      .select('*')
      .eq('pool_id', poolId)
      .order('score', { ascending: false });

    setPoolMembers(data || []);
  };

  const createPool = async () => {
    if (!user || !newPoolName) return;

    try {
      const inviteCode = Math.random().toString(36).substring(2, 8).toUpperCase();
      
      const { data: pool, error } = await supabase
        .from('betting_pools')
        .insert({
          name: newPoolName,
          description: newPoolDesc,
          creator_id: user.id,
          pool_type: 'private',
          invite_code: inviteCode,
          status: 'active',
        })
        .select()
        .single();

      if (error) throw error;

      // Add creator as owner
      await supabase.from('pool_members').insert({
        pool_id: pool.id,
        user_id: user.id,
        role: 'owner',
      });

      toast.success('Pool created! Share the invite code with friends.');
      setCreateDialogOpen(false);
      setNewPoolName('');
      setNewPoolDesc('');
      fetchPools();
    } catch (error) {
      toast.error('Failed to create pool');
    }
  };

  const joinPool = async (poolId?: string) => {
    if (!user) {
      toast.error('Please sign in to join a pool');
      return;
    }

    try {
      let targetPoolId = poolId;

      if (!targetPoolId && joinCode) {
        // Find pool by invite code
        const { data: pool } = await supabase
          .from('betting_pools')
          .select('id')
          .eq('invite_code', joinCode.toUpperCase())
          .single();

        if (!pool) {
          toast.error('Invalid invite code');
          return;
        }
        targetPoolId = pool.id;
      }

      if (!targetPoolId) return;

      const { error } = await supabase.from('pool_members').insert({
        pool_id: targetPoolId,
        user_id: user.id,
        role: 'member',
      });

      if (error) {
        if (error.code === '23505') {
          toast.error('You are already in this pool');
        } else {
          throw error;
        }
        return;
      }

      toast.success('Successfully joined the pool!');
      setJoinCode('');
      fetchPools();
    } catch (error) {
      toast.error('Failed to join pool');
    }
  };

  const copyInviteCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast.success('Invite code copied!');
  };

  const PoolCard = ({ pool, isMember = false }: { pool: BettingPool; isMember?: boolean }) => (
    <Card className="bg-card/50 border-border/50 hover:border-primary/50 transition-colors cursor-pointer"
      onClick={() => {
        setSelectedPool(pool);
        fetchPoolMembers(pool.id);
      }}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            {pool.pool_type === 'private' ? (
              <Lock className="h-4 w-4 text-muted-foreground" />
            ) : (
              <Globe className="h-4 w-4 text-green-500" />
            )}
            <h4 className="font-semibold">{pool.name}</h4>
          </div>
          <Badge variant={pool.status === 'active' ? 'default' : 'secondary'}>
            {pool.status}
          </Badge>
        </div>
        
        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
          {pool.description || 'No description'}
        </p>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 text-sm">
            <span className="flex items-center gap-1">
              <Users className="h-4 w-4" />
              {pool.max_members}
            </span>
            {pool.prize_pool > 0 && (
              <span className="flex items-center gap-1 text-primary">
                <Trophy className="h-4 w-4" />
                ${pool.prize_pool}
              </span>
            )}
          </div>
          
          {!isMember && (
            <Button size="sm" onClick={(e) => { e.stopPropagation(); joinPool(pool.id); }}>
              Join
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Card className="bg-card/50 backdrop-blur border-border/50">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-1/3" />
            <div className="grid grid-cols-2 gap-4">
              <div className="h-32 bg-muted rounded" />
              <div className="h-32 bg-muted rounded" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6 text-primary" />
            Betting Pools
          </h2>
          <p className="text-muted-foreground">Compete with friends and community</p>
        </div>

        <div className="flex gap-2 flex-wrap">
          {/* Join by code */}
          <div className="flex gap-2">
            <Input
              placeholder="Enter invite code"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value)}
              className="w-36"
            />
            <Button variant="outline" onClick={() => joinPool()}>
              Join
            </Button>
          </div>

          {/* Create pool */}
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Create Pool
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create a Betting Pool</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <Input
                  placeholder="Pool name"
                  value={newPoolName}
                  onChange={(e) => setNewPoolName(e.target.value)}
                />
                <Input
                  placeholder="Description (optional)"
                  value={newPoolDesc}
                  onChange={(e) => setNewPoolDesc(e.target.value)}
                />
                <Button className="w-full" onClick={createPool}>
                  Create Pool
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs defaultValue="my-pools" className="space-y-4">
        <TabsList>
          <TabsTrigger value="my-pools">My Pools</TabsTrigger>
          <TabsTrigger value="public">Public Pools</TabsTrigger>
        </TabsList>

        <TabsContent value="my-pools" className="space-y-4">
          {myPools.length === 0 ? (
            <Card className="bg-card/50 border-border/50">
              <CardContent className="p-8 text-center">
                <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-semibold mb-2">No pools yet</h3>
                <p className="text-muted-foreground mb-4">
                  Create a pool or join one with an invite code
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {myPools.map((pool) => (
                <PoolCard key={pool.id} pool={pool} isMember />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="public" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pools.map((pool) => (
              <PoolCard key={pool.id} pool={pool} />
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Pool Details Dialog */}
      <Dialog open={!!selectedPool} onOpenChange={(open) => !open && setSelectedPool(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-primary" />
              {selectedPool?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-muted-foreground">{selectedPool?.description}</p>
            
            {selectedPool?.invite_code && (
              <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-lg">
                <span className="text-sm">Invite Code:</span>
                <code className="font-mono font-bold">{selectedPool.invite_code}</code>
                <Button size="sm" variant="ghost" onClick={() => copyInviteCode(selectedPool.invite_code)}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            )}

            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Medal className="h-4 w-4" />
                Leaderboard
              </h4>
              <div className="space-y-2">
                {poolMembers.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No members yet</p>
                ) : (
                  poolMembers.map((member, idx) => (
                    <div key={member.id} className="flex items-center justify-between p-2 rounded bg-muted/30">
                      <div className="flex items-center gap-3">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${
                          idx === 0 ? 'bg-yellow-500 text-black' :
                          idx === 1 ? 'bg-gray-400 text-black' :
                          idx === 2 ? 'bg-amber-700 text-white' :
                          'bg-muted text-muted-foreground'
                        }`}>
                          {idx + 1}
                        </span>
                        <span>{member.user_id.slice(0, 8)}...</span>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="flex items-center gap-1">
                          <TrendingUp className="h-3 w-3" />
                          {member.score.toFixed(1)}%
                        </span>
                        <span className="text-muted-foreground">
                          {member.winning_bets}/{member.total_bets}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
